import React from "react";

import {
  mdiAntenna,
  mdiCreditCardOutline,
  mdiMovie,
  mdiTelevisionClassic,
  mdiVideo,
} from "@mdi/js";
import Icon from "@mdi/react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import HighlightExternalFastLiveConfig from "./HighlightExternalFastLiveConfig";
import HighlightHostedConfig from "./HighlightHostedConfig";
import HighlightOfferConfig from "./HighlightOfferConfig";
import HighlightType from "./HighlightType";
import SwitchOnOff from "../../../../components/common/tools/SwitchOnOff";
import ConditionalRenderEditor from "../ConditionalRenderEditor";

import type { JSX } from "react";

import type {
  ConditionRender,
  HighlightParams,
} from "../../../../api/ProjectAPI.types";
import type { PlatformRoutesParams } from "../../../../components/routes/PlatformRoutes";
import type { HighlightContentType } from "../../../../constants/highlight";

interface HighlightConfigProps {
  componentParams: HighlightParams;
  onChange: (params: Partial<HighlightParams>) => void;
  onChangeContentType: (contentTypeSelected: HighlightContentType) => void;
}

function HighlightConfig({
  componentParams,
  onChange,
  onChangeContentType,
}: HighlightConfigProps): JSX.Element {
  const { t } = useTranslation("editor-highlight");
  const { contentType, isDescriptionDisplayed, isTitleDisplayed } =
    componentParams;
  const { platform = "web" } = useParams<PlatformRoutesParams>();

  const isTypeHostedSelected = contentType === "hosted";
  const isTypeLiveSelected = contentType === "live";
  const isTypeExternalSelected = contentType === "external";
  const isTypeFastSelected = contentType === "fast";
  const isTypeOfferSelected = contentType === "offer";

  const getComponentHighlightTypeConfig = (): JSX.Element | null => {
    switch (contentType) {
      case "hosted":
        return (
          <HighlightHostedConfig
            componentParams={componentParams}
            onChange={onChange}
          />
        );
      case "external":
      case "fast":
      case "live":
        return (
          <HighlightExternalFastLiveConfig
            componentParams={componentParams}
            onChange={onChange}
          />
        );
      case "offer":
        return (
          <HighlightOfferConfig
            componentParams={componentParams}
            onChange={onChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <div className="mb-[0.625rem] flex flex-col pt-[0.625rem]">
        <div className="py-[0.625rem] text-[0.9375rem] font-semibold">
          {t("contentType")}
        </div>
        <div className="flex flex-wrap items-center">
          <HighlightType
            icon={<Icon path={mdiVideo} size="30px" />}
            isSelected={isTypeHostedSelected}
            onChange={(): void => onChangeContentType("hosted")}
            title={t("hosted")}
          />
          <HighlightType
            icon={<Icon path={mdiAntenna} size="30px" />}
            isSelected={isTypeLiveSelected}
            onChange={(): void => onChangeContentType("live")}
            title={t("live")}
          />
          <HighlightType
            icon={<Icon path={mdiMovie} size="30px" />}
            isSelected={isTypeExternalSelected}
            onChange={(): void => onChangeContentType("external")}
            title={t("external")}
          />
          <HighlightType
            icon={<Icon path={mdiTelevisionClassic} size="30px" />}
            isSelected={isTypeFastSelected}
            onChange={(): void => onChangeContentType("fast")}
            title={t("fast")}
          />
          <HighlightType
            icon={<Icon path={mdiCreditCardOutline} size="30px" />}
            isDisabled={platform !== "web"}
            isSelected={isTypeOfferSelected}
            title={t("offer")}
            onChange={(): void =>
              platform === "web" ? onChangeContentType("offer") : undefined
            }
          />
        </div>
        {getComponentHighlightTypeConfig()}
        <div className="border-b-border my-[1.5625rem] w-full border-b" />

        <div className="py-[0.625rem] text-[0.9375rem] font-semibold">
          {t("highlightSettings")}
        </div>

        <div className="m-[0.3125rem] flex items-center gap-[0.625rem]">
          <SwitchOnOff
            checked={isTitleDisplayed}
            name="display-title"
            size="sm"
            onChange={(value: boolean): void =>
              onChange({ isTitleDisplayed: value })
            }
          />
          <div>{t("displayTitle")}</div>
        </div>
        <div className="m-[0.3125rem] flex items-center gap-[0.625rem]">
          <SwitchOnOff
            checked={isDescriptionDisplayed}
            name="display-short-description"
            size="sm"
            onChange={(value: boolean): void =>
              onChange({ isDescriptionDisplayed: value })
            }
          />
          <div>{t("displayDescription")}</div>
        </div>
        {!isTypeOfferSelected && (
          <div className="m-[0.3125rem] flex items-center gap-[0.625rem]">
            <SwitchOnOff
              checked={componentParams.isButtonDisplayed}
              name="display-button"
              size="sm"
              onChange={(value: boolean): void =>
                onChange({ isButtonDisplayed: value })
              }
            />
            <div>{t("displayButton")}</div>
          </div>
        )}
        {isTypeFastSelected || isTypeExternalSelected ? (
          <div className="m-[0.3125rem] flex items-center gap-[0.625rem]">
            <SwitchOnOff
              checked={componentParams.isEpgDisplayed}
              name="display-epg"
              size="sm"
              onChange={(value: boolean): void =>
                onChange({ isEpgDisplayed: value })
              }
            />
            <div>{t("displayEpg")}</div>
          </div>
        ) : null}
        {!isTypeOfferSelected && (
          <div className="m-[0.3125rem] flex items-center gap-[0.625rem]">
            <SwitchOnOff
              checked={componentParams.isClickableComponent}
              name="component-clickable"
              size="sm"
              onChange={(value: boolean): void =>
                onChange({ isClickableComponent: value })
              }
            />
            <div>{t("clickableVideo")}</div>
          </div>
        )}
        {isTypeHostedSelected ? (
          <div className="m-[0.3125rem] flex items-center gap-[0.625rem]">
            <SwitchOnOff
              checked={componentParams.isLoopVideo}
              name="loop-video"
              size="sm"
              onChange={(value: boolean): void =>
                onChange({ isLoopVideo: value })
              }
            />
            <div>{t("loopVideo")}</div>
          </div>
        ) : null}
        <div className="border-b-border my-[1.5625rem] w-full border-b" />
        <ConditionalRenderEditor
          componentParams={componentParams}
          onChangeConditions={(value: ConditionRender[]): void =>
            onChange({ conditional_render: value })
          }
          onChangeConditionsToggle={(value: boolean): void => {
            onChange({ has_conditions: value });
          }}
        />
      </div>
    </div>
  );
}

export default HighlightConfig;
